import { useEffect, useRef, useState } from "react";
import { useGetProfileDataQuery } from "../lib/api/apiSlice";

interface ProfileData {
	[key: string]: any;
}

interface ApiResponse {
	success: boolean;
	data: ProfileData | string;
	timestamp: string;
	request_id: string;
}

export const useProfileData = (
	accessTokenParam?: string | null,
) => {
	const startTimeRef = useRef<number | null>(null);
	const errorRef = useRef<Error | undefined>(undefined);
	const maxPollTime = 180000; // 3 minutes
	const pollInterval = 7000; // 7 seconds

	// Always start with false to match SSR (no localStorage during SSR)
	// Use accessTokenParam if provided (from props, not localStorage)
	const [hasToken, setHasToken] = useState(!!accessTokenParam);

	// Update token availability when param changes or when token is stored (client-side only)
	useEffect(() => {
		const checkToken = () => {
			const token = accessTokenParam || (typeof window !== "undefined" && localStorage.getItem("accessToken"));
			setHasToken(!!token);
		};

		// Check immediately on mount (client-side)
		checkToken();

		// Listen for token storage events
		if (typeof window !== "undefined") {
			window.addEventListener("tokenStored", checkToken);
			return () => {
				window.removeEventListener("tokenStored", checkToken);
			};
		}
	}, [accessTokenParam]);

	// Check if we should enable polling
	const shouldPoll = (data: ApiResponse | undefined, error: Error | undefined) => {
		// Don't poll if we have data
		if (data?.success && data.data) {
			return false;
		}
		// Don't poll if there's an error (except if it's just "no data yet")
		if (error && !error.message.includes("not found")) {
			return false;
		}
		// Don't poll if we've exceeded max time
		if (startTimeRef.current && Date.now() - startTimeRef.current >= maxPollTime) {
			return false;
		}
		return true;
	};

	const { data, error, isLoading, refetch } = useGetProfileDataQuery(undefined, {
		skip: !hasToken,
	});

	// Manual polling logic using useEffect
	useEffect(() => {
		if (!hasToken) return;

		// Start timer on first fetch
		if (!startTimeRef.current && !isLoading) {
			startTimeRef.current = Date.now();
		}

		// Check if we should continue polling
		if (!shouldPoll(data, errorRef.current)) {
			startTimeRef.current = null;
			return;
		}

		// Check if we've exceeded max time
		if (startTimeRef.current && Date.now() - startTimeRef.current >= maxPollTime) {
			startTimeRef.current = null;
			return;
		}

		const intervalId = setInterval(() => {
			if (shouldPoll(data, errorRef.current)) {
				refetch();
			} else {
				startTimeRef.current = null;
				clearInterval(intervalId);
			}
		}, pollInterval);

		return () => clearInterval(intervalId);
	}, [hasToken, isLoading, data, refetch]);

	// Update error ref
	useEffect(() => {
		if (error) {
			errorRef.current = error as Error;
		} else {
			errorRef.current = undefined;
		}
	}, [error]);

	// Check if we're currently polling
	const isPolling = shouldPoll(data, error as Error | undefined) && isLoading;

	// Handle timeout error
	useEffect(() => {
		if (startTimeRef.current && Date.now() - startTimeRef.current >= maxPollTime) {
			if (!data?.success || !data?.data) {
				// Timeout reached without data
				startTimeRef.current = null;
			}
		}
	}, [data, maxPollTime]);

	// Parse data if it's a string, otherwise use as-is
	const parsedData = data?.success && data.data 
		? (typeof data.data === 'string' ? JSON.parse(data.data) : data.data)
		: null;

	return {
		profileData: parsedData ? { rawData: parsedData } : null,
		loading: isLoading,
		error: error ? (error as any).message || String(error) : null,
		isPolling,
	};
};
